/**
 * Map measured `model_perf_runs` onto the Performance latency table.
 *
 * Seeded `performance_metrics` (POST /performance-metrics) is the platform
 * catalog / static baseline column. A matching measured run is additive:
 * either a "Measured {preset}" column with latency numbers, or an explicit
 * washout message when the run stored error counts and null latencies.
 * An empty catalog is expected for unseeded base models and is not a
 * failed serving-perf run.
 */

export const LATENCY_STAT_ROWS = Object.freeze([
  { key: 'min', metric: 'Min' },
  { key: 'median', metric: 'Median' },
  { key: 'avg', metric: 'Avg' },
  { key: 'p90', metric: 'P90' },
  { key: 'p95', metric: 'P95' },
  { key: 'max', metric: 'Max' },
]);

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function _formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function _sameText(left, right) {
  if (left == null || right == null) return true;
  return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
}

export function latencyBlockHasValues(latency) {
  if (!latency || typeof latency !== 'object') return false;
  return LATENCY_STAT_ROWS.some(({ key }) => latency[key] != null);
}

export function emptyLatencyRows() {
  return LATENCY_STAT_ROWS.map(({ key, metric }) => ({
    key,
    metric,
    current: null,
    comparison: null,
    measured: null,
    unit: 'ms',
  }));
}

export function concurrencyPoints(run) {
  return Array.isArray(run?.concurrency_points) ? run.concurrency_points : [];
}

export function matchingConcurrencyPoint(run, concurrency) {
  const points = concurrencyPoints(run);
  if (!points.length) return null;
  if (concurrency == null) return points[0];
  const wanted = Number(concurrency);
  return points.find((point) => Number(point?.concurrency) === wanted) || null;
}

export function totalRunErrors(run, concurrency = null) {
  const points = concurrencyPoints(run);
  const scoped =
    concurrency == null
      ? points
      : points.filter((point) => Number(point?.concurrency) === Number(concurrency));
  return scoped.reduce((sum, point) => sum + (Number(point?.errors) || 0), 0);
}

export function successfulInferenceCount(run, concurrency = null) {
  const points = concurrencyPoints(run);
  const scoped =
    concurrency == null
      ? points
      : points.filter((point) => Number(point?.concurrency) === Number(concurrency));
  return scoped.reduce((sum, point) => {
    if (latencyBlockHasValues(point?.latency_ms)) {
      return sum + Math.max(Number(point?.tokens_in) || 0, 1);
    }
    return sum;
  }, 0);
}

export function runMatchesSku(run, filters) {
  if (!run) return false;
  const env = filters?.environments?.[0];
  if (!env) return true;
  if (env.cloudProvider && !_sameText(run.cloud_provider, env.cloudProvider)) {
    return false;
  }
  if (env.instanceType && !_sameText(run.instance_type, env.instanceType)) {
    return false;
  }
  return true;
}

/**
 * Latest terminal measured run for this version + SKU that includes the
 * selected concurrency (or has any points when the run washed out).
 */
export function selectMeasuredRunForTable(runs, filters) {
  const concurrency = filters?.concurrency;
  const matching = (Array.isArray(runs) ? runs : []).filter((run) => {
    if (!TERMINAL_STATUSES.has(run?.status)) return false;
    if (!runMatchesSku(run, filters)) return false;
    const points = concurrencyPoints(run);
    if (!points.length) return run.status === 'failed' || run.status === 'completed';
    if (concurrency == null) return true;
    if (points.some((point) => Number(point?.concurrency) === Number(concurrency))) {
      return true;
    }
    const levels = run.concurrency_levels || [];
    return levels.some((level) => Number(level) === Number(concurrency));
  });
  return matching[0] || null;
}

function _trimmedLabel(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

/**
 * Header for the seeded `performance_metrics` column. Source first so an
 * empty catalog cannot be read as "this version failed".
 */
export function catalogColumnLabel(modelLabel) {
  const name = _trimmedLabel(modelLabel);
  return name ? `Catalog · ${name}` : 'Catalog baseline';
}

/**
 * Header for the additive `model_perf_runs` column.
 */
export function measuredColumnLabel(run) {
  if (!run) return 'Measured serving-perf';
  const preset = typeof run.preset === 'string' && run.preset.trim()
    ? `${run.preset.charAt(0).toUpperCase()}${run.preset.slice(1)}`
    : null;
  return preset ? `Measured ${preset}` : 'Measured serving-perf';
}

/**
 * Empty catalog is expected for many base models (e.g. distilbert). Do not
 * say "No data" — that reads as a failed V4 / empty measured run.
 */
export function catalogEmptyStateText(baseModelName) {
  const name = _trimmedLabel(baseModelName);
  if (name) {
    return `${name} has no catalog baseline. That is not a failed serving-perf run.`;
  }
  return 'This base model has no catalog baseline. That is not a failed serving-perf run.';
}

export function latencyTableEmptyText(measuredOutcome, baseModelName) {
  if (measuredOutcome?.message) return measuredOutcome.message;
  return catalogEmptyStateText(baseModelName);
}

export function historyStatusLabel(outcome) {
  if (!outcome) return 'Unknown';
  if (outcome.kind === 'in_progress') return outcome.status || 'Running';
  if (outcome.kind === 'washout') return 'Completed with errors';
  if (outcome.kind === 'partial') return 'Completed with errors';
  if (outcome.kind === 'failed') {
    if (/interrupted|abandoned/i.test(outcome.message || '')) return 'Interrupted';
    return 'Failed';
  }
  if (outcome.kind === 'cancelled') return 'Cancelled';
  if (outcome.status === 'completed') return 'Completed';
  return outcome.status || 'Unknown';
}

export function historyStatusVariant(outcome) {
  if (!outcome) return 'default';
  if (outcome.kind === 'in_progress') return 'processing';
  if (outcome.kind === 'ok') return 'success';
  if (outcome.kind === 'cancelled') return 'default';
  return 'error';
}

/**
 * @returns {{
 *   kind: 'in_progress'|'ok'|'partial'|'washout'|'failed'|'cancelled'|'empty',
 *   tone: 'error'|'warning'|'processing'|null,
 *   message: string|null,
 *   hasLatency: boolean,
 *   errors: number,
 *   successful: number,
 *   status: string,
 *   runId: string|null,
 * }}
 */
export function describeMeasuredRunOutcome(run, concurrency = null) {
  if (!run) {
    return {
      kind: 'empty',
      tone: null,
      message: null,
      hasLatency: false,
      errors: 0,
      successful: 0,
      status: '',
      runId: null,
    };
  }
  const status = run.status || '';
  const errors = totalRunErrors(run, concurrency);
  const successful = successfulInferenceCount(run, concurrency);
  const point = matchingConcurrencyPoint(run, concurrency);
  const hasLatency = concurrency == null
    ? concurrencyPoints(run).some((item) => latencyBlockHasValues(item?.latency_ms))
    : latencyBlockHasValues(point?.latency_ms);
  const runId = run.run_id || null;
  const base = {
    hasLatency,
    errors,
    successful,
    status,
    runId,
  };

  if (status === 'queued' || status === 'provisioning' || status === 'running') {
    return {
      ...base,
      kind: 'in_progress',
      tone: 'processing',
      message: `Benchmark is ${status}.`,
    };
  }
  if (status === 'cancelled') {
    return {
      ...base,
      kind: 'cancelled',
      tone: null,
      message: run.error || 'Benchmark was cancelled.',
    };
  }
  if (status === 'failed' && !hasLatency) {
    return {
      ...base,
      kind: 'failed',
      tone: 'error',
      message: run.error || 'Benchmark failed; no latency recorded.',
    };
  }

  if (!hasLatency && (errors > 0 || successful === 0)) {
    const countLabel = errors > 0
      ? `${_formatCount(errors)} failed requests`
      : 'no successful inferences';
    const suffix = run.error
      ? ` ${run.error}`
      : ' Triton was not Ready for the whole budget.';
    return {
      ...base,
      kind: 'washout',
      tone: 'error',
      message: `Completed with ${countLabel}; no latency recorded.${suffix}`,
    };
  }

  if (hasLatency && errors > 0) {
    return {
      ...base,
      kind: 'partial',
      tone: 'warning',
      message: `Completed with ${_formatCount(errors)} failed requests; latency is from the successful calls only.`,
    };
  }

  return {
    ...base,
    kind: 'ok',
    tone: null,
    message: null,
  };
}

export function mergeMeasuredIntoLatencyRows(catalogRows, run, concurrency) {
  const hasCatalogRows = Array.isArray(catalogRows) && catalogRows.length > 0;
  if (!run && !hasCatalogRows) return [];
  const base = hasCatalogRows
    ? catalogRows.map((row) => ({ ...row, measured: row.measured ?? null }))
    : emptyLatencyRows();
  if (!run) return base;
  const point = matchingConcurrencyPoint(run, concurrency);
  const latency = point?.latency_ms || {};
  return base.map((row) => ({
    ...row,
    measured: latency[row.key] ?? null,
  }));
}
